import React, { useState, useRef, useEffect } from 'react';
import Viewer3D from './components/Viewer3D';
import Toolbar from './components/Toolbar';
import Sidebar from './components/Sidebar';
import FileImporter from './components/FileImporter';
import ShapeLibrary from './components/ShapeLibrary';
import TextGenerator from './components/TextGenerator';
import InspectorPanel from './components/InspectorPanel';
import { exportModel } from './utils/exporters';
import { handleError } from './utils/errors';

export default function App() {
  const viewerRef = useRef(null);
  
  const [theme, setTheme] = useState('light');
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const [bgColor, setBgColor] = useState('#eef3f8');
  const [showGrid, setShowGrid] = useState(true);
  const [showAxes, setShowAxes] = useState(false);
  const [wireframe, setWireframe] = useState(false);
  const [showBuildPlate, setShowBuildPlate] = useState(true);
  const [buildPlateSize, setBuildPlateSize] = useState(200);
  const [transformMode, setTransformMode] = useState('translate');
  const [hasModel, setHasModel] = useState(false);
  const [selectedObject, setSelectedObject] = useState(null);
  const [selectedInfo, setSelectedInfo] = useState(null);
  const [exportName, setExportName] = useState('model');
  
  const [stats, setStats] = useState({
    triangles: 0,
    vertices: 0,
    size: { x: 0, y: 0, z: 0 },
    volume: 0
  });
  
  const [notification, setNotification] = useState(null);

  const handleThemeToggle = () => {
    setTheme((currentTheme) => {
      const nextTheme = currentTheme === 'light' ? 'dark' : 'light';
      setBgColor(nextTheme === 'light' ? '#eef3f8' : '#0d1624');
      return nextTheme;
    });
  };

  // Show notification
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Handle file load
  const handleFileLoad = (result) => {
    if (!viewerRef.current) return;
    
    try {
      result.mesh.name = result.fileName?.replace(/\.[^/.]+$/, '') || 'Imported model';
      result.mesh.userData = {
        ...result.mesh.userData,
        type: 'Imported model',
        name: result.mesh.name
      };
      viewerRef.current.clearScene();
      viewerRef.current.addObject(result.mesh);
      setHasModel(true);
      setExportName(result.fileName?.replace(/\.[^/.]+$/, '') || 'model');
      showNotification(`Loaded ${result.fileName} successfully`, 'success');
    } catch (error) {
      handleErrorDisplay(error);
    }
  };

  // Handle shape addition
  const handleAddShape = (result) => {
    if (!viewerRef.current) return;
    
    try {
      viewerRef.current.addObject(result.mesh);
      setHasModel(true);
      showNotification(`Added ${result.type} shape`, 'success');
    } catch (error) {
      handleErrorDisplay(error);
    }
  };

  // Handle text addition
  const handleAddText = (result) => {
    if (!viewerRef.current) return;
    
    try {
      viewerRef.current.addObject(result.mesh);
      setHasModel(true);
      showNotification('Generated 3D text successfully', 'success');
    } catch (error) {
      handleErrorDisplay(error);
    }
  };

  // Handle export
  const handleExport = async (format) => {
    if (!viewerRef.current) return;
    
    try {
      const modelGroup = viewerRef.current.getModelGroup();
      if (!modelGroup || modelGroup.children.length === 0) {
        showNotification('No model to export', 'error');
        return;
      }

      showNotification(`Exporting as ${format.toUpperCase()}...`, 'info');
      await exportModel(modelGroup, format, exportName);
      showNotification(`Exported successfully as ${format.toUpperCase()}`, 'success');
    } catch (error) {
      handleErrorDisplay(error);
    }
  };

  // Handle screenshot
  const handleScreenshot = () => {
    if (!viewerRef.current) return;
    
    try {
      viewerRef.current.takeScreenshot(`${exportName}_screenshot.png`);
      showNotification('Screenshot saved', 'success');
    } catch (error) {
      handleErrorDisplay(error);
    }
  };

  // Handle reset camera
  const handleResetCamera = () => {
    if (!viewerRef.current) return;
    viewerRef.current.resetCamera();
  };

  // Handle clear scene
  const handleClearScene = () => {
    if (!viewerRef.current) return;

    viewerRef.current.clearScene();
    setHasModel(false);
    setSelectedObject(null);
    setSelectedInfo(null);
    showNotification('Scene cleared', 'info');
  };

  // Handle place on platform
  const handlePlaceOnPlatform = () => {
    if (!viewerRef.current || !selectedObject) return;
    viewerRef.current.placeOnPlatform();
    setSelectedInfo(viewerRef.current.getSelectedObjectInfo());
    showNotification('Object placed on platform', 'success');
  };

  // Handle delete selected
  const handleDeleteSelected = () => {
    if (!viewerRef.current || !selectedObject) return;

    viewerRef.current.deleteSelected();
    setSelectedObject(null);
    setSelectedInfo(null);
    showNotification('Object deleted', 'info');
  };

  const handleDuplicateSelected = () => {
    if (!viewerRef.current || !selectedObject) return;
    viewerRef.current.duplicateSelected();
    showNotification('Object duplicated', 'success');
  };

  const handleApplyTransform = (patch) => {
    if (!viewerRef.current || !selectedObject) return;
    viewerRef.current.updateSelectedTransform(patch);
    setSelectedInfo(viewerRef.current.getSelectedObjectInfo());
    showNotification('Transform applied', 'success');
  };

  const handleApplyDimensions = (dimensions, lockAspect) => {
    if (!viewerRef.current || !selectedObject) return;
    viewerRef.current.setSelectedDimensions(dimensions, lockAspect);
    setSelectedInfo(viewerRef.current.getSelectedObjectInfo());
    showNotification('Model resized', 'success');
  };

  const handleFrameSelection = () => {
    if (!viewerRef.current) return;
    viewerRef.current.frameSelected();
  };

  // Handle object selection
  const handleObjectSelected = (obj, info = null) => {
    setSelectedObject(obj);
    setSelectedInfo(info);
  };

  // Handle stats update
  const handleStatsUpdate = (newStats) => {
    setStats(newStats);
    setHasModel(newStats.triangles > 0 || newStats.vertices > 0);
  };

  // Handle error display
  const handleErrorDisplay = (error) => {
    const errorInfo = handleError(error, 'App');
    showNotification(errorInfo.message, 'error');
    console.error('Error:', errorInfo);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if typing in input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      switch (e.key.toLowerCase()) {
        case 'f':
          handleResetCamera();
          break;
        case 'g':
          setShowGrid(prev => !prev);
          break;
        case 'w':
          setWireframe(prev => !prev);
          break;
        case 'p':
          if (selectedObject) handlePlaceOnPlatform();
          break;
        case 'delete':
        case 'backspace':
          if (selectedObject) {
            e.preventDefault();
            handleDeleteSelected();
          }
          break;
        case 't':
          setTransformMode('translate');
          break;
        case 'r':
          setTransformMode('rotate');
          break;
        case 's':
          setTransformMode('scale');
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasModel, selectedObject]);

  return (
    <div className={`app-shell theme-${theme}`}>
      {/* Toolbar */}
      <Toolbar
        onExport={handleExport}
        onScreenshot={handleScreenshot}
        onResetCamera={handleResetCamera}
        onClearScene={handleClearScene}
        bgColor={bgColor}
        onBgColorChange={setBgColor}
        hasModel={hasModel}
        theme={theme}
        leftPanelCollapsed={leftPanelCollapsed}
        rightPanelCollapsed={rightPanelCollapsed}
        onThemeToggle={handleThemeToggle}
        onToggleLeftPanel={() => setLeftPanelCollapsed((current) => !current)}
        onToggleRightPanel={() => setRightPanelCollapsed((current) => !current)}
      />

      {/* Main Content */}
      <div className={`workspace-shell ${leftPanelCollapsed ? 'left-collapsed' : ''} ${rightPanelCollapsed ? 'right-collapsed' : ''}`}>
        {/* Sidebar */}
        {!leftPanelCollapsed && (
          <Sidebar>
            <FileImporter
              onFileLoad={handleFileLoad}
              onError={handleErrorDisplay}
            />
            <ShapeLibrary
              onAddShape={handleAddShape}
              onError={handleErrorDisplay}
            />
            <TextGenerator
              onAddText={handleAddText}
              onError={handleErrorDisplay}
            />
          </Sidebar>
        )}

        {/* 3D Viewer */}
        <main className="viewer-shell">
          <Viewer3D
            ref={viewerRef}
            bgColor={bgColor}
            theme={theme}
            showGrid={showGrid}
            showAxes={showAxes}
            wireframe={wireframe}
            showBuildPlate={showBuildPlate}
            buildPlateSize={buildPlateSize}
            transformMode={transformMode}
            onStatsUpdate={handleStatsUpdate}
            onError={handleErrorDisplay}
            onObjectSelected={handleObjectSelected}
          />

          {/* Notification */}
          {notification && (
            <div className={`notification fade-in ${
              notification.type === 'error' ? 'error-message' :
              notification.type === 'success' ? 'success-message' :
              'info-message'
            }`}>
              {notification.message}
            </div>
          )}

          {!hasModel && (
            <div className="canvas-status">Empty workplane</div>
          )}

          <div className="viewport-chip">
            {selectedInfo ? selectedInfo.name : hasModel ? 'Scene ready' : 'Ready'}
          </div>
        </main>

        {!rightPanelCollapsed && (
          <InspectorPanel
            selectedInfo={selectedInfo}
            selectedObject={selectedObject}
            modelGroup={viewerRef.current?.getModelGroup()}
            stats={stats}
            transformMode={transformMode}
            showGrid={showGrid}
            showAxes={showAxes}
            wireframe={wireframe}
            showBuildPlate={showBuildPlate}
            buildPlateSize={buildPlateSize}
            onTransformModeChange={setTransformMode}
            onToggleGrid={setShowGrid}
            onToggleAxes={setShowAxes}
            onToggleWireframe={setWireframe}
            onToggleBuildPlate={setShowBuildPlate}
            onBuildPlateSizeChange={setBuildPlateSize}
            onApplyTransform={handleApplyTransform}
            onApplyDimensions={handleApplyDimensions}
            onPlaceOnPlatform={handlePlaceOnPlatform}
            onDuplicateSelected={handleDuplicateSelected}
            onDeleteSelected={handleDeleteSelected}
            onFrameSelection={handleFrameSelection}
            onMaterialUpdate={() => showNotification('Material applied', 'success')}
          />
        )}
      </div>
    </div>
  );
}

